import { Billboard, ContactShadows, Html, OrbitControls, useGLTF, useTexture } from "@react-three/drei";
import { Canvas, useThree } from "@react-three/fiber";
import {
  Camera,
  Cuboid,
  Download,
  Expand,
  Eye,
  Focus,
  Moon,
  Rotate3D,
  Sun,
  Undo2
} from "lucide-react";
import {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentRef
} from "react";
import * as THREE from "three";
import {
  facadeMaterials,
  roofMaterials,
  windowMaterials
} from "../data";
import { useConfiguratorStore } from "../store";
import type { CameraView } from "../types";
import { FloorPlanViewer } from "./FloorPlanViewer";

interface HouseViewerProps {
  modelUrl: string;
  cadStatus: "idle" | "loading" | "success" | "error";
  onRetry: () => void;
}

const cameraPositions: Record<CameraView, [number, number, number]> = {
  perspective: [17, 8.5, 21],
  front: [0, 5.5, 28],
  back: [0, 5.5, -28],
  left: [-28, 5.5, 0],
  right: [28, 5.5, 0],
  top: [0.1, 31, 0.1]
};

function CameraController() {
  const camera = useThree((state) => state.camera);
  const carport = useConfiguratorStore((state) => state.configuration.carport);
  const controlsRef = useRef<ComponentRef<typeof OrbitControls>>(null);
  const targetX = carport ? 1.7 : 0;

  useEffect(() => {
    const changeView = (event: Event) => {
      const view = (event as CustomEvent<CameraView>).detail;
      const [x, y, z] = cameraPositions[view];
      camera.position.set(x, y, z);
      camera.lookAt(targetX, 2, 0);
      controlsRef.current?.target.set(targetX, 2, 0);
      controlsRef.current?.update();
    };

    window.addEventListener("cbciso-camera", changeView);
    return () => window.removeEventListener("cbciso-camera", changeView);
  }, [camera, targetX]);

  useEffect(() => {
    controlsRef.current?.target.set(targetX, 2, 0);
    controlsRef.current?.update();
  }, [targetX]);

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      target={[targetX, 2, 0]}
      minDistance={9}
      maxDistance={45}
      minPolarAngle={0.2}
      enableDamping
      dampingFactor={0.06}
      enablePan
      maxPolarAngle={Math.PI / 2.02}
    />
  );
}

function HouseModel({ url }: { url: string }) {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const { scene } = useGLTF(url);
  const clone = useMemo(() => {
    const copy = scene.clone(true);
    copy.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      if (Array.isArray(mesh.material)) {
        mesh.material = mesh.material.map((material) => material.clone());
      } else {
        mesh.material = mesh.material.clone();
      }
    });
    return copy;
  }, [scene]);

  useEffect(() => {
    const facade = facadeMaterials.find((item) => item.id === configuration.facadeMaterialId);
    const roof = roofMaterials.find((item) => item.id === configuration.roofMaterialId);
    const windows = windowMaterials.find((item) => item.id === configuration.windowColorId);

    clone.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      if (!(material instanceof THREE.MeshStandardMaterial)) return;

      if (mesh.name.startsWith("Exterior_Facade") && facade) {
        material.color.set(facade.color);
        material.roughness = facade.texture === "stone" ? 0.92 : 0.78;
        material.envMapIntensity = facade.texture === "stone" ? 0.55 : 0.75;
      }
      if (mesh.name.startsWith("Exterior_Roof") && roof) {
        material.color.set(roof.color);
        material.roughness = roof.texture === "metal" ? 0.3 : 0.82;
        material.metalness = roof.texture === "metal" ? 0.56 : 0.02;
        material.envMapIntensity = roof.texture === "metal" ? 1.35 : 0.6;
      }
      if (mesh.name.startsWith("Exterior_WindowFrames") && windows) {
        material.color.set(windows.color);
        material.metalness = 0.72;
        material.roughness = 0.24;
        material.envMapIntensity = 1.4;
      }
      if (mesh.name.startsWith("Window_Glass") && material instanceof THREE.MeshPhysicalMaterial) {
        material.color.set("#8da7a2");
        material.roughness = 0.1;
        material.metalness = 0;
        material.transmission = 0.22;
        material.thickness = 0.08;
        material.ior = 1.45;
        material.opacity = 0.82;
        material.transparent = true;
        material.envMapIntensity = 1.8;
      }
      if (mesh.name.startsWith("Terrace")) mesh.visible = configuration.terrace;
      if (mesh.name.startsWith("Carport")) mesh.visible = configuration.carport;
    });
  }, [
    clone,
    configuration.carport,
    configuration.facadeMaterialId,
    configuration.roofMaterialId,
    configuration.terrace,
    configuration.windowColorId
  ]);

  useEffect(
    () => () => {
      clone.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (!mesh.isMesh) return;
        mesh.geometry.dispose();
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materials.forEach((material) => material.dispose());
      });
    },
    [clone]
  );

  return <primitive object={clone} position={[0, 0.02, 0]} />;
}

function RenderSettings({ night }: { night: boolean }) {
  const gl = useThree((state) => state.gl);
  const scene = useThree((state) => state.scene);
  const invalidate = useThree((state) => state.invalidate);

  useEffect(() => {
    gl.outputColorSpace = THREE.SRGBColorSpace;
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    gl.toneMappingExposure = night ? 0.64 : 1.08;
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = THREE.PCFSoftShadowMap;
    scene.environmentIntensity = night ? 0.22 : 0.7;
    invalidate();
  }, [gl, invalidate, night, scene]);

  return null;
}

function EnvironmentBackdrop({ night }: { night: boolean }) {
  const texture = useTexture("/environments/treeline-v1.webp?v=1");
  const scene = useThree((state) => state.scene);
  const backdropTexture = useMemo(() => {
    const copy = texture.clone();
    copy.colorSpace = THREE.SRGBColorSpace;
    copy.wrapS = THREE.RepeatWrapping;
    copy.repeat.set(5, 1);
    copy.anisotropy = 8;
    copy.needsUpdate = true;
    return copy;
  }, [texture]);

  useEffect(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.needsUpdate = true;
    const previousEnvironment = scene.environment;
    texture.mapping = THREE.EquirectangularReflectionMapping;
    scene.environment = texture;
    return () => {
      scene.environment = previousEnvironment;
    };
  }, [scene, texture]);

  useEffect(() => () => backdropTexture.dispose(), [backdropTexture]);

  return (
    <mesh position={[0, 8.5, 0]} rotation={[0, Math.PI * 0.18, 0]}>
      <cylinderGeometry args={[43, 43, 29, 64, 1, true]} />
      <meshBasicMaterial
        map={backdropTexture}
        color={night ? "#304055" : "#ffffff"}
        side={THREE.BackSide}
        toneMapped={false}
        fog={false}
      />
    </mesh>
  );
}

function TreeBillboard({
  kind,
  position,
  height,
  tint = "#ffffff"
}: {
  kind: "oak" | "birch";
  position: [number, number, number];
  height: number;
  tint?: string;
}) {
  const texture = useTexture(`/trees/${kind}-final.webp?v=1`);
  const width = height * (kind === "oak" ? 0.67 : 0.6);

  useEffect(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.needsUpdate = true;
  }, [texture]);

  return (
    <Billboard position={[position[0], position[1] + height / 2, position[2]]} follow>
      <mesh>
        <planeGeometry args={[width, height]} />
        <meshBasicMaterial
          map={texture}
          color={tint}
          alphaTest={0.16}
          transparent
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>
    </Billboard>
  );
}

function Landscape({ night }: { night: boolean }) {
  const shrubs = [
    [-7, 0.35, -3], [-5.7, 0.3, -3.5], [6.8, 0.4, -3.1],
    [7.8, 0.32, -2.4], [-7.5, 0.38, 2.2], [7.4, 0.3, 2.7]
  ] as const;
  const trees = [
    { kind: "oak" as const, position: [-18, 0, -13] as [number, number, number], height: 12.5, tint: night ? "#65746f" : "#f4f7eb" },
    { kind: "birch" as const, position: [-12, 0, -17] as [number, number, number], height: 11.5, tint: night ? "#61716e" : "#eef5e8" },
    { kind: "oak" as const, position: [-5, 0, -20] as [number, number, number], height: 13.5, tint: night ? "#586863" : "#e9f1df" },
    { kind: "birch" as const, position: [4, 0, -21] as [number, number, number], height: 12, tint: night ? "#60706c" : "#f1f5e8" },
    { kind: "oak" as const, position: [12, 0, -17] as [number, number, number], height: 14, tint: night ? "#56655f" : "#edf3e5" },
    { kind: "birch" as const, position: [19, 0, -12] as [number, number, number], height: 11.8, tint: night ? "#667570" : "#f3f6ea" }
  ];

  return (
    <>
      <Suspense fallback={null}>
        <EnvironmentBackdrop night={night} />
      </Suspense>
      <mesh rotation-x={-Math.PI / 2} receiveShadow position-y={-0.04}>
        <planeGeometry args={[80, 64]} />
        <meshStandardMaterial color={night ? "#263c30" : "#668653"} roughness={0.98} envMapIntensity={0.22} />
      </mesh>
      <mesh rotation-x={-Math.PI / 2} receiveShadow position={[0, -0.01, 5.6]}>
        <planeGeometry args={[17, 4.2]} />
        <meshStandardMaterial color="#bbb8ae" roughness={0.88} envMapIntensity={0.35} />
      </mesh>
      <Suspense fallback={null}>
        {trees.map((tree, index) => <TreeBillboard key={index} {...tree} />)}
      </Suspense>
      {shrubs.map(([x, y, z], index) => (
        <group key={index} position={[x, y, z]}>
          <mesh castShadow receiveShadow>
            <icosahedronGeometry args={[0.58 + (index % 2) * 0.14, 2]} />
            <meshStandardMaterial color={index % 2 ? "#3f6238" : "#587744"} roughness={0.96} envMapIntensity={0.28} />
          </mesh>
          <mesh position={[0.5, -0.14, 0.15]} castShadow receiveShadow>
            <icosahedronGeometry args={[0.35, 2]} />
            <meshStandardMaterial color="#6f8251" roughness={0.96} envMapIntensity={0.25} />
          </mesh>
        </group>
      ))}
      <ContactShadows position={[0, 0.02, 0]} opacity={night ? 0.4 : 0.34} scale={30} blur={2.8} far={14} resolution={512} />
    </>
  );
}

function Scene({ modelUrl, night }: { modelUrl: string; night: boolean }) {
  return (
    <>
      <RenderSettings night={night} />
      <color attach="background" args={[night ? "#1f2933" : "#b8d9ec"]} />
      <fog attach="fog" args={[night ? "#28333b" : "#c5d9cf", 38, 70]} />
      <hemisphereLight args={[night ? "#7f95bd" : "#e4f1ff", night ? "#18251e" : "#566f45", night ? 0.72 : 1.55]} />
      <ambientLight intensity={night ? 0.16 : 0.28} />
      <directionalLight
        position={night ? [-9, 14, -6] : [12, 18, 10]}
        intensity={night ? 1.8 : 4.1}
        color={night ? "#adc3ff" : "#fff4dc"}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-near={1}
        shadow-camera-far={55}
        shadow-camera-left={-24}
        shadow-camera-right={24}
        shadow-camera-top={22}
        shadow-camera-bottom={-22}
        shadow-bias={-0.00012}
        shadow-normalBias={0.035}
      />
      {night && (
        <>
          <pointLight position={[-4, 2.4, 4.1]} intensity={14} distance={11} color="#ffc879" />
          <pointLight position={[4, 2.4, 4.1]} intensity={12} distance={10} color="#ffd59b" />
        </>
      )}
      <Suspense fallback={<Html center><div className="canvas-loader">Klargør huset…</div></Html>}>
        <HouseModel url={modelUrl} />
      </Suspense>
      <Landscape night={night} />
      <CameraController />
    </>
  );
}

const dispatchCamera = (view: CameraView) => {
  window.dispatchEvent(new CustomEvent<CameraView>("cbciso-camera", { detail: view }));
};

function downloadScreenshot() {
  const canvas = document.querySelector<HTMLCanvasElement>(".house-canvas canvas");
  if (!canvas) return;
  const link = document.createElement("a");
  link.download = "mit-cbciso-hus.png";
  link.href = canvas.toDataURL("image/png");
  link.click();
}

export function HouseViewer({ modelUrl, cadStatus, onRetry }: HouseViewerProps) {
  const mode = useConfiguratorStore((state) => state.viewerMode);
  const setMode = useConfiguratorStore((state) => state.setViewerMode);
  const undo = useConfiguratorStore((state) => state.undo);
  const canUndo = useConfiguratorStore((state) => state.history.length > 0);
  const [night, setNight] = useState(false);
  const [anglesOpen, setAnglesOpen] = useState(false);
  const viewerRef = useRef<HTMLElement>(null);

  return (
    <main className={`viewer-shell ${night ? "night" : ""}`} ref={viewerRef}>
      <div className="view-switch" role="tablist" aria-label="Visning">
        <button className={mode === "3d" ? "active" : ""} type="button" onClick={() => setMode("3d")} role="tab" aria-selected={mode === "3d"}>
          <Cuboid size={16} /> 3D
        </button>
        <button className={mode === "2d" ? "active" : ""} type="button" onClick={() => setMode("2d")} role="tab" aria-selected={mode === "2d"}>
          <Focus size={16} /> 2D
        </button>
      </div>

      {mode === "3d" ? (
        <div className="house-canvas">
          <Canvas
            camera={{ position: cameraPositions.perspective, fov: 37, near: 0.1, far: 100 }}
            shadows
            dpr={[1, 1.65]}
            frameloop="demand"
            gl={{ antialias: true, preserveDrawingBuffer: true }}
          >
            <Scene modelUrl={modelUrl} night={night} />
          </Canvas>
          <div className="rotate-hint"><Rotate3D size={17} /> Træk for at dreje</div>
        </div>
      ) : (
        <FloorPlanViewer />
      )}

      <div className="viewer-tools">
        <button type="button" aria-label={night ? "Skift til dagslys" : "Skift til aftenlys"} title={night ? "Dagslys" : "Aftenlys"} onClick={() => setNight((value) => !value)}>
          {night ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <button type="button" aria-label="Vælg kameravinkel" title="Kameravinkel" onClick={() => setAnglesOpen((value) => !value)}>
          <Eye size={18} />
        </button>
        <button type="button" aria-label="Nulstil kamera" title="Nulstil kamera" onClick={() => dispatchCamera("perspective")}>
          <Camera size={18} />
        </button>
        <button type="button" aria-label="Udvid visning" title="Fuld skærm" onClick={() => viewerRef.current?.requestFullscreen()}>
          <Expand size={18} />
        </button>
      </div>

      {anglesOpen && (
        <div className="camera-menu">
          {(["perspective", "front", "back", "left", "right", "top"] as CameraView[]).map((view) => (
            <button key={view} type="button" onClick={() => { dispatchCamera(view); setAnglesOpen(false); }}>
              {({ perspective: "Perspektiv", front: "Forfra", back: "Bagfra", left: "Venstre", right: "Højre", top: "Oppefra" })[view]}
            </button>
          ))}
        </div>
      )}

      <div className="viewer-bottom-tools">
        <button type="button" disabled={!canUndo} onClick={undo}><Undo2 size={16} /> Fortryd</button>
        <button type="button" onClick={downloadScreenshot}><Download size={16} /> Gem billede</button>
      </div>

      {cadStatus === "loading" && (
        <div className="cad-status" role="status">
          <span className="status-spinner" />
          <span><strong>Opdaterer huset</strong><small>CAD-preview genereres…</small></span>
        </div>
      )}
      {cadStatus === "success" && <div className="cad-success" role="status">Dit hus er opdateret</div>}
      {cadStatus === "error" && (
        <div className="cad-error" role="alert">
          <span>Visningen kunne ikke opdateres. Dine valg er gemt.</span>
          <button type="button" onClick={onRetry}>Prøv igen</button>
        </div>
      )}
    </main>
  );
}
